<script setup lang="ts">
import flagGB from '~/assets/svg/flag-gb.svg?url';
import flagSE from '~/assets/svg/flag-se.svg?url';
import flagDE from '~/assets/svg/flag-de.svg?url';
import flagPT from '~/assets/svg/flag-pt.svg?url';

const props = defineProps({
    id: {
        type: String,
        default: () => '',
    },
    class: {
        type: String,
        default: () => '',
    },
});

const { locale } = useI18n();
const route = useRoute();
const { market, language: currentLanguage } = useMarket();
const { marketToAvailableLocales, localeToLanguage } = useAppConfig();

// Language configuration with flags
const languageConfig: Record<Language, { name: string; icon: string; defaultLocale: Locale }> = {
    en: { name: 'English', icon: flagGB, defaultLocale: 'en-gb' },
    sv: { name: 'Swedish', icon: flagSE, defaultLocale: 'sv-se' },
    de: { name: 'Deutsch', icon: flagDE, defaultLocale: 'de-de' },
    pt: { name: 'Portugues', icon: flagPT, defaultLocale: 'pt-pt' },
};

// Current language display
const currentLanguageDisplay = computed(() => {
    return languageConfig[currentLanguage.value] || languageConfig.en;
});

// All languages (always show all)
const allLanguages: Language[] = ['en', 'sv', 'de', 'pt'];

// Build locale for a given language - prefer current market, fallback to language's default
function getLocaleForLanguage(lang: Language): Locale {
    const availableLocales = marketToAvailableLocales[market.value] || [];
    // First, try to find this language in the current market
    for (const loc of availableLocales) {
        if (localeToLanguage[loc as Locale] === lang) {
            return loc as Locale;
        }
    }
    // If not available in current market, use language's default locale (switches market)
    return languageConfig[lang].defaultLocale;
}

// Build the path for switching to a different language
function getLanguageSwitchPath(lang: Language): string {
    const newLocale = getLocaleForLanguage(lang);
    // Get current path without locale prefix
    const path = route.path;
    const currentLocale = locale.value;

    // Remove current locale prefix and add new one
    let pathWithoutLocale = path;
    if (path.startsWith(`/${currentLocale}/`)) {
        pathWithoutLocale = path.substring(currentLocale.length + 1);
    } else if (path === `/${currentLocale}`) {
        pathWithoutLocale = '/home';
    }

    // Ensure path starts with /
    if (!pathWithoutLocale.startsWith('/')) {
        pathWithoutLocale = '/' + pathWithoutLocale;
    }

    return `/${newLocale}${pathWithoutLocale}`;
}

function closeDropdown() {
    const element = document.getElementById(`language-dropdown-${props.id}`);
    if (element) {
        element.classList.remove('block');
        element.classList.add('hidden');
    }
}
</script>

<template>
    <button
        type="button"
        :data-dropdown-toggle="`language-dropdown-${id}`"
        :class="`${props.class} flex sm:inline-flex items-center ${
            id === 'footer' ? 'language-dropdown-footer' : 'language-dropdown'
        }`"
    >
        <img
            class="h-5 w-5 mr-2"
            :src="currentLanguageDisplay.icon"
            :alt="`${currentLanguageDisplay.name} flag`"
        />
        {{ currentLanguageDisplay.name }}
        <svg
            class="ml-1 w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
            ></path>
        </svg>
    </button>

    <div
        :class="`hidden ${
            id === 'footer' ? 'language-dropdown-area-footer' : 'language-dropdown-area'
        }`"
        :id="`language-dropdown-${id}`"
        @click="closeDropdown"
    >
        <ul class="py-1" role="none">
            <li v-for="lang in allLanguages" :key="lang">
                <a
                    :href="getLanguageSwitchPath(lang)"
                    :class="`${
                        id === 'footer' ? 'language-dropdown-item-footer' : 'language-dropdown-item'
                    }`"
                    role="menuitem"
                >
                    <div class="inline-flex items-center">
                        <img
                            class="h-3.5 w-3.5 mr-2"
                            :src="languageConfig[lang].icon"
                            :alt="`${languageConfig[lang].name} flag`"
                        />
                        {{ languageConfig[lang].name }}
                    </div>
                </a>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.language-dropdown {
    @apply text-gray-800 dark:text-gray-300 hover:bg-gray-50 focus:ring-gray-300 font-medium rounded-lg text-sm px-2.5 lg:px-5 py-2.5 mr-2 dark:hover:bg-gray-800;
}

.language-dropdown-footer {
    @apply text-gray-300 focus:ring-gray-300 font-medium rounded-lg text-sm px-2.5 lg:px-5 py-2.5 mr-2 hover:bg-gray-800;
}

.language-dropdown-area {
    @apply z-50 my-4 w-48 text-base list-none bg-white rounded divide-y divide-gray-100 shadow-xl dark:bg-gray-800;
}

.language-dropdown-area-footer {
    @apply z-50 my-4 w-48 text-base list-none rounded divide-y divide-gray-100 shadow-xl bg-gray-800;
}

.language-dropdown-item {
    @apply block py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white;
}

.language-dropdown-item-footer {
    @apply block py-2 px-4 text-sm text-gray-400 hover:bg-gray-600 hover:text-white;
}
</style>
